// Persistent Cache Service using IndexedDB
// Provides a unified interface for storing and retrieving cached data across browser sessions

class PersistentCacheService {
  private dbName = 'playgroundCache';
  private dbVersion = 7;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private writeEpoch = 0;
  private activeUserScope: string | null | undefined;
  private writesSuspended = false;

  constructor() {
    this.initPromise = this.initDB();
  }

  /**
   * Fence writes that began under a previous authenticated browser scope.
   *
   * Call this before clearing caches during logout/account switching. A writer
   * that was still awaiting IndexedDB initialization then cannot open a new
   * transaction after the clear and repopulate old-user data.
   */
  invalidatePendingWrites(): void {
    this.writeEpoch += 1;
  }

  /**
   * Start an authentication-scope transition before clearing browser state.
   *
   * Writes remain suspended until the newly keyed auth-scoped provider mounts.
   * This closes the interval where the Firebase client has switched identity
   * but React is still tearing down effects from the previous account.
   */
  beginUserScopeTransition(nextScopeId: string | null): void {
    this.invalidatePendingWrites();
    this.activeUserScope = nextScopeId;
    this.writesSuspended = true;
  }

  /**
   * Activate the scope rendered by the auth-keyed application subtree.
   * Signed-out production views intentionally keep persistence suspended.
   */
  activateUserScope(scopeId: string | null): void {
    if (this.activeUserScope !== undefined && this.activeUserScope !== scopeId) {
      this.invalidatePendingWrites();
    }
    this.activeUserScope = scopeId;
    this.writesSuspended = scopeId === null;
  }

  captureWriteEpoch(): number {
    return this.writeEpoch;
  }

  isWriteAllowed(scopeId: string | null | undefined, epoch: number): boolean {
    if (epoch !== this.writeEpoch || this.writesSuspended) {
      return false;
    }
    return (
      scopeId === undefined
      || this.activeUserScope === undefined
      || scopeId === this.activeUserScope
    );
  }

  private async writableDB(
    epoch: number,
    scopeId?: string | null,
  ): Promise<IDBDatabase | null> {
    if (!this.isWriteAllowed(scopeId, epoch)) {
      return null;
    }
    const db = await this.ensureDB();
    return this.isWriteAllowed(scopeId, epoch) ? db : null;
  }

  private buildSearchMediaScopePrefix(scopeId: string): string {
    const normalizedScope = String(scopeId || '').trim() || '__anonymous__';
    return `${normalizedScope.length}:${normalizedScope}:`;
  }

  private buildSearchImagePayloadKey(scopeId: string, token: string): string {
    return `${this.buildSearchMediaScopePrefix(scopeId)}${token}`;
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        resolve();
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);
      let settled = false;
      let blockedTimer: ReturnType<typeof setTimeout> | null = null;
      const openTimeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        console.warn('IndexedDB open timed out; falling back to volatile cache mode.');
        resolve();
      }, 3000);

      const resolveOnce = () => {
        if (settled) return;
        settled = true;
        clearTimeout(openTimeout);
        if (blockedTimer) {
          clearTimeout(blockedTimer);
        }
        resolve();
      };

      const rejectOnce = (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(openTimeout);
        if (blockedTimer) {
          clearTimeout(blockedTimer);
        }
        reject(error);
      };

      request.onerror = () => rejectOnce(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        resolveOnce();
      };

      request.onblocked = () => {
        if (blockedTimer) return;
        console.warn('IndexedDB upgrade is blocked by another tab; using volatile cache mode until unblocked.');
        blockedTimer = setTimeout(() => {
          resolveOnce();
        }, 800);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // These legacy stores held expiring media URLs, preload payloads, or
        // result-bearing session details. Removing them frees existing browser
        // storage and prevents new sessions from recreating data that is now
        // intentionally memory-only.
        [
          'gifCache',
          'segmentCache',
          'videoData',
          'searchResultsMedia',
          'agenticSessionDetails',
        ].forEach((storeName) => {
          if (db.objectStoreNames.contains(storeName)) {
            db.deleteObjectStore(storeName);
          }
        });

        // Create object stores if they don't exist

        if (!db.objectStoreNames.contains('metadata')) {
          const metadataStore = db.createObjectStore('metadata', { keyPath: 'key' });
          metadataStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('searchImagePayloads')) {
          const imagePayloadStore = db.createObjectStore('searchImagePayloads', { keyPath: 'token' });
          imagePayloadStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('agenticSessionSummaries')) {
          const summariesStore = db.createObjectStore('agenticSessionSummaries', { keyPath: 'cacheKey' });
          summariesStore.createIndex('userId', 'userId', { unique: false });
          summariesStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          summariesStore.createIndex('cachedAt', 'cachedAt', { unique: false });
          summariesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('agenticCacheMeta')) {
          const metaStore = db.createObjectStore('agenticCacheMeta', { keyPath: 'userId' });
          metaStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          metaStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initPromise;
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  private async awaitTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  private buildAgenticCacheKey(userId: string, sessionId: string): string {
    return `${userId}::${sessionId}`;
  }

  private async getAllByIndex<T>(
    storeName: string,
    indexName: string,
    value: string,
  ): Promise<T[]> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);

      return await new Promise<T[]>((resolve) => {
        const request = index.getAll(value);
        request.onsuccess = () => resolve((request.result || []) as T[]);
        request.onerror = () => resolve([]);
      });
    } catch {
      return [];
    }
  }

  // Metadata operations
  async getMetadata(key: string): Promise<any | null> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['metadata'], 'readonly');
      const store = transaction.objectStore('metadata');
      
      return new Promise((resolve) => {
        const request = store.get(key);
        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.value : null);
        };
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  async setMetadata(
    key: string,
    value: any,
    scopeId?: string | null,
  ): Promise<void> {
    const writeEpoch = this.writeEpoch;
    try {
      const db = await this.writableDB(writeEpoch, scopeId);
      if (!db) return;
      const transaction = db.transaction(['metadata'], 'readwrite');
      const store = transaction.objectStore('metadata');

      const entry = {
        key,
        value,
        timestamp: Date.now()
      };

      store.put(entry);
      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to persist metadata:', error);
    }
  }

  async getSearchImagePayload(
    token: string,
    scopeId: string = '__anonymous__',
  ): Promise<
    | { status: 'found'; item: { payload: string; createdAt: number } }
    | { status: 'missing' }
    | { status: 'unavailable' }
  > {
    if (!token) return { status: 'missing' };
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['searchImagePayloads'], 'readonly');
      const store = transaction.objectStore('searchImagePayloads');

      return new Promise((resolve) => {
        const request = store.get(
          this.buildSearchImagePayloadKey(scopeId, token),
        );
        request.onsuccess = () => {
          const result = request.result;
          if (!result || typeof result.payload !== 'string') {
            resolve({ status: 'missing' });
            return;
          }
          resolve({
            status: 'found',
            item: {
              payload: result.payload,
              createdAt: typeof result.createdAt === 'number' ? result.createdAt : Date.now(),
            },
          });
        };
        request.onerror = () => resolve({ status: 'unavailable' });
      });
    } catch {
      return { status: 'unavailable' };
    }
  }

  async setSearchImagePayload(
    token: string,
    payload: string,
    createdAt: number,
    scopeId: string = '__anonymous__',
  ): Promise<boolean> {
    if (!token || !payload) return false;
    const writeEpoch = this.writeEpoch;
    try {
      const db = await this.writableDB(writeEpoch, scopeId);
      if (!db) return false;
      const transaction = db.transaction(['searchImagePayloads'], 'readwrite');
      const store = transaction.objectStore('searchImagePayloads');
      store.put({
        token: this.buildSearchImagePayloadKey(scopeId, token),
        payload,
        createdAt,
        timestamp: Date.now(),
      });
      await this.awaitTransaction(transaction);
      return true;
    } catch (error) {
      console.warn('Failed to persist search image payload:', error);
      return false;
    }
  }

  async removeSearchImagePayload(
    token: string,
    scopeId: string = '__anonymous__',
  ): Promise<void> {
    if (!token) return;
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['searchImagePayloads'], 'readwrite');
      const store = transaction.objectStore('searchImagePayloads');
      store.delete(this.buildSearchImagePayloadKey(scopeId, token));
      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to remove search image payload:', error);
    }
  }

  async pruneSearchImagePayloads(
    maxItems: number,
    maxAgeMs: number,
    scopeId: string = '__anonymous__',
  ): Promise<void> {
    const safeMaxItems = Math.max(maxItems, 1);
    const cutoff = Date.now() - Math.max(maxAgeMs, 0);
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['searchImagePayloads'], 'readwrite');
      const store = transaction.objectStore('searchImagePayloads');
      const prefix = this.buildSearchMediaScopePrefix(scopeId);
      const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
      const retained: Array<{ token: string; createdAt: number }> = [];

      await new Promise<void>((resolve, reject) => {
        const request = store.openCursor(range);
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
          if (!cursor) {
            retained
              .sort((left, right) => left.createdAt - right.createdAt)
              .slice(0, Math.max(retained.length - safeMaxItems, 0))
              .forEach(({ token }) => store.delete(token));
            resolve();
            return;
          }

          const token = String(cursor.primaryKey);
          const entry = cursor.value as { createdAt?: number; timestamp?: number } | undefined;
          const createdAt = typeof entry?.createdAt === 'number'
            ? entry.createdAt
            : typeof entry?.timestamp === 'number'
              ? entry.timestamp
              : 0;

          if (createdAt < cutoff) {
            cursor.delete();
          } else {
            retained.push({ token, createdAt });
          }
          cursor.continue();
        };
        request.onerror = () => reject(request.error);
      });

      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to prune search image payloads:', error);
    }
  }

  async clearSearchImagePayloads(scopeId?: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['searchImagePayloads'], 'readwrite');
      const store = transaction.objectStore('searchImagePayloads');
      if (!scopeId) {
        store.clear();
      } else {
        const prefix = this.buildSearchMediaScopePrefix(scopeId);
        const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
        const request = store.openKeyCursor(range);
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          store.delete(cursor.primaryKey);
          cursor.continue();
        };
      }
      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to clear search image payloads:', error);
    }
  }

  async getAgenticSessionSummaries<T = any>(userId: string): Promise<T[]> {
    if (!userId) return [];
    const rows = await this.getAllByIndex<{
      data?: T;
      updatedAt?: string;
    }>('agenticSessionSummaries', 'userId', userId);
    return rows
      .sort((left, right) => String(right.updatedAt || '').localeCompare(String(left.updatedAt || '')))
      .map((row) => row.data)
      .filter((row): row is T => row !== undefined && row !== null);
  }

  async setAgenticSessionSummaries<T extends { session_id: string; updated_at: string }>(
    userId: string,
    summaries: T[],
    options: { replaceMissing?: boolean } = {},
  ): Promise<void> {
    if (!userId) return;
    const writeEpoch = this.writeEpoch;
    const { replaceMissing = false } = options;
    try {
      const db = await this.writableDB(writeEpoch, userId);
      if (!db) return;
      const existing = replaceMissing
        ? await this.getAllByIndex<{ cacheKey: string; sessionId: string }>(
            'agenticSessionSummaries',
            'userId',
            userId,
          )
        : [];
      if (!this.isWriteAllowed(userId, writeEpoch)) {
        return;
      }
      const incomingIds = replaceMissing
        ? new Set(summaries.map((summary) => summary.session_id))
        : null;

      const transaction = db.transaction(['agenticSessionSummaries'], 'readwrite');
      const store = transaction.objectStore('agenticSessionSummaries');

      existing.forEach((entry) => {
        if (incomingIds && !incomingIds.has(entry.sessionId)) {
          store.delete(entry.cacheKey);
        }
      });

      summaries.forEach((summary) => {
        store.put({
          cacheKey: this.buildAgenticCacheKey(userId, summary.session_id),
          userId,
          sessionId: summary.session_id,
          updatedAt: summary.updated_at,
          data: summary,
          cachedAt: Date.now(),
          timestamp: Date.now(),
        });
      });

      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to persist agentic session summaries:', error);
    }
  }

  async removeAgenticSession(userId: string, sessionId: string): Promise<void> {
    if (!userId || !sessionId) return;
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['agenticSessionSummaries'], 'readwrite');
      transaction.objectStore('agenticSessionSummaries').delete(this.buildAgenticCacheKey(userId, sessionId));
      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to remove agentic session from cache:', error);
    }
  }

  async getAgenticCacheMeta<T = any>(userId: string): Promise<T | null> {
    if (!userId) return null;
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['agenticCacheMeta'], 'readonly');
      const store = transaction.objectStore('agenticCacheMeta');
      const request = store.get(userId);
      return await new Promise<T | null>((resolve) => {
        request.onsuccess = () => resolve((request.result as T) || null);
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  async setAgenticCacheMeta(
    userId: string,
    value: { schemaVersion: number; cacheInstanceId: string; updatedAt?: number },
  ): Promise<void> {
    if (!userId) return;
    const writeEpoch = this.writeEpoch;
    try {
      const db = await this.writableDB(writeEpoch, userId);
      if (!db) return;
      const transaction = db.transaction(['agenticCacheMeta'], 'readwrite');
      const store = transaction.objectStore('agenticCacheMeta');
      store.put({
        userId,
        schemaVersion: value.schemaVersion,
        cacheInstanceId: value.cacheInstanceId,
        updatedAt: value.updatedAt ?? Date.now(),
        timestamp: Date.now(),
      });
      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to persist agentic cache metadata:', error);
    }
  }

  async clearAgenticCache(userId?: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const storeNames = ['agenticSessionSummaries', 'agenticCacheMeta'];

      if (!userId) {
        const transaction = db.transaction(storeNames, 'readwrite');
        storeNames.forEach((storeName) => transaction.objectStore(storeName).clear());
        await this.awaitTransaction(transaction);
        return;
      }

      const summaryRows = await this.getAllByIndex<{ cacheKey: string }>('agenticSessionSummaries', 'userId', userId);
      const transaction = db.transaction(storeNames, 'readwrite');
      const summariesStore = transaction.objectStore('agenticSessionSummaries');
      const metaStore = transaction.objectStore('agenticCacheMeta');

      summaryRows.forEach((row) => summariesStore.delete(row.cacheKey));
      metaStore.delete(userId);

      await this.awaitTransaction(transaction);
    } catch (error) {
      console.warn('Failed to clear agentic cache:', error);
    }
  }

  // Clear all caches (used on user switch)
  async clearAll(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const stores = [
        'metadata',
        'searchImagePayloads',
        'agenticSessionSummaries',
        'agenticCacheMeta',
      ].filter((storeName) => db.objectStoreNames.contains(storeName));

      if (stores.length === 0) {
        return;
      }

      const transaction = db.transaction(stores, 'readwrite');
      stores.forEach((storeName) => {
        transaction.objectStore(storeName).clear();
      });
      await this.awaitTransaction(transaction);
    } catch (error) {
      // Account transitions must fail closed when durable, user-scoped data
      // cannot be cleared. Swallowing this error lets AuthContext publish the
      // next identity while unscoped legacy cache rows remain readable.
      console.warn('Failed to clear all caches:', error);
      throw error;
    }
  }

  // Clean up old entries based on age
  async cleanupOldEntries(maxAge: number): Promise<void> {
    try {
      const db = await this.ensureDB();
      const cutoffTime = Date.now() - maxAge;
      
      // Clean each store
      const stores = [
        'metadata',
        'searchImagePayloads',
        'agenticSessionSummaries',
        'agenticCacheMeta',
      ];
      
      for (const storeName of stores) {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const index = store.index('timestamp');
        
        const range = IDBKeyRange.upperBound(cutoffTime);
        const request = index.openCursor(range);
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            cursor.continue();
          }
        };
        await this.awaitTransaction(transaction);
      }
    } catch (error) {
      console.warn('Failed to cleanup old entries:', error);
    }
  }
}

// Export singleton instance
export const persistentCache = new PersistentCacheService();
